import type {
    AddCommerceEnrollmentProductRequest,
    BeginCommerceEnrollmentRequest,
    CommerceEnrollmentSession,
    CommerceOperationOptions,
} from "@teqtank/commerce-contracts";
import { CommerceRepository } from "../repositories/commerce-repository.js";
import { beginCommerceEnrollmentOperation } from "../operations/begin-commerce-enrollment-operation.js";
import { getCommerceEnrollmentOperation } from "../operations/get-commerce-enrollment-operation.js";
import { selectCommerceEnrollmentKitOperation } from "../operations/select-commerce-enrollment-kit-operation.js";
import { completeCommerceEnrollmentStageOperation } from "../operations/complete-commerce-enrollment-stage-operation.js";
import { abandonCommerceEnrollmentOperation } from "../operations/abandon-commerce-enrollment-operation.js";
import { addCommerceEnrollmentProductOperation } from "../operations/add-commerce-enrollment-product-operation.js";
import { returnCommerceEnrollmentToShoppingOperation } from "../operations/return-commerce-enrollment-to-shopping-operation.js";
import { resolveCommerceEnrollment } from "./enrollment-response-resolver.js";

export class CommerceEnrollmentRepository extends CommerceRepository {
    async begin(
        request: BeginCommerceEnrollmentRequest,
        options: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession> {
        return resolveCommerceEnrollment(
            (
                await this.execute(
                    beginCommerceEnrollmentOperation,
                    request,
                    options,
                    "enrollment-operation-failed",
                    "Teqtank Commerce could not begin Enrollment.",
                )
            ).enrollment,
        );
    }
    async get(
        enrollmentId: string | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession | null> {
        return resolveCommerceEnrollment(
            (
                await this.execute(
                    getCommerceEnrollmentOperation,
                    enrollmentId ? { enrollmentId } : {},
                    options,
                    "enrollment-operation-failed",
                    "Teqtank Commerce could not retrieve Enrollment.",
                )
            ).enrollment,
        );
    }
    async selectKit(
        enrollment: CommerceEnrollmentSession,
        kitId: string,
        options: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession> {
        return resolveCommerceEnrollment(
            (
                await this.execute(
                    selectCommerceEnrollmentKitOperation,
                    { enrollmentId: enrollment.id, kitId, expectedVersion: enrollment.version },
                    options,
                    "enrollment-operation-failed",
                    "Teqtank Commerce could not select that enrollment collection.",
                )
            ).enrollment,
        );
    }
    async addProduct(
        enrollment: CommerceEnrollmentSession,
        request: Omit<AddCommerceEnrollmentProductRequest, "enrollmentId" | "expectedVersion">,
        options: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession> {
        return resolveCommerceEnrollment(
            (
                await this.execute(
                    addCommerceEnrollmentProductOperation,
                    {
                        ...request,
                        enrollmentId: enrollment.id,
                        expectedVersion: enrollment.version,
                    },
                    options,
                    "enrollment-operation-failed",
                    "Teqtank Commerce could not add that product to Enrollment.",
                )
            ).enrollment,
        );
    }
    async completeStage(
        enrollment: CommerceEnrollmentSession,
        outcome: string,
        options: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession> {
        return resolveCommerceEnrollment(
            (
                await this.execute(
                    completeCommerceEnrollmentStageOperation,
                    {
                        enrollmentId: enrollment.id,
                        stageId: enrollment.currentStage.id,
                        outcome,
                        expectedVersion: enrollment.version,
                    },
                    options,
                    "enrollment-operation-failed",
                    "Teqtank Commerce could not continue Enrollment.",
                )
            ).enrollment,
        );
    }
    async returnToShopping(options: CommerceOperationOptions): Promise<CommerceEnrollmentSession> {
        return resolveCommerceEnrollment(
            (
                await this.execute(
                    returnCommerceEnrollmentToShoppingOperation,
                    {},
                    options,
                    "enrollment-operation-failed",
                    "Teqtank Commerce could not return to the enrollment selection.",
                )
            ).enrollment,
        );
    }
    async abandon(
        enrollment: CommerceEnrollmentSession,
        options: CommerceOperationOptions,
    ): Promise<void> {
        await this.execute(
            abandonCommerceEnrollmentOperation,
            { enrollmentId: enrollment.id, expectedVersion: enrollment.version },
            options,
            "enrollment-operation-failed",
            "Teqtank Commerce could not abandon Enrollment.",
        );
    }
}
